import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AccountV2Service,
  SavedSearch,
  SavedSearchEstimate,
  SavedSearchMatches,
  SavedSearchPlate,
} from 'src/app/services/account-v2-service';
import { SessionService } from 'src/app/services/session-service';
import { localDevAccess } from 'src/app/dev-access';
import { termOf } from './saved-search-criteria';

type LoadState = 'loading' | 'ready' | 'error';

/**
 * Where a digest's "See all N" and a card's "View N new" land (plan step 4):
 * the plates this search matched, newest first, each marked new if it arrived
 * after the last visit. Opening the page records the visit afterwards, so the
 * markers reflect the previous one. With no matches yet it shows what's for
 * sale now (the estimate sample) instead of an empty page.
 *
 * Where the API keeps no match lists (production's legacy alerts), the page
 * hands over to an ordinary search for the search's word.
 */
@Component({
  selector: 'pla-saved-search-results',
  templateUrl: './saved-search-results.component.html',
  styleUrls: ['./saved-search-results.component.scss'],
})
export class SavedSearchResultsComponent implements OnInit {
  public id = 0;
  public state: LoadState = 'loading';
  public search: SavedSearch | null = null;
  public matches: SavedSearchMatches | null = null;
  public forSale: SavedSearchEstimate | null = null;
  public showOnlyNew = false;
  public page = 1;
  public readonly pageSize = 50;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private session: SessionService,
    private account: AccountV2Service,
  ) { }

  ngOnInit(): void {
    this.id = Number(this.route.snapshot.paramMap.get('id'));
    if (!this.session.isLoggedIn() && !localDevAccess) {
      this.session.setSuccessRoute(`/saved-search/${this.id}`);
      this.router.navigate(['/login']);
      return;
    }
    this.account.getCapabilities().subscribe({
      next: (caps) => (caps.searchMatches ? this.load() : this.searchInstead()),
      error: () => (this.state = 'error'),
    });
  }

  private searchInstead(): void {
    this.account.getSearches().subscribe({
      next: (searches) => {
        const term = termOf(searches.find((s) => s.id === this.id)?.criteria || '');
        this.router.navigate(term ? ['/search-results', term] : ['/account/searches'], { replaceUrl: true });
      },
      error: () => (this.state = 'error'),
    });
  }

  public load(): void {
    this.state = 'loading';
    this.account.getSearches().subscribe({
      next: (searches) => {
        this.search = searches.find((s) => s.id === this.id) || null;
        if (!this.search) { this.router.navigate(['/account/searches']); return; }
        this.loadMatches(true);
      },
      error: () => (this.state = 'error'),
    });
  }

  private loadMatches(first: boolean): void {
    this.account.getSearchMatches(this.id, this.page, this.pageSize).subscribe({
      next: (m) => {
        this.matches = m;
        this.state = 'ready';
        if (first) this.account.touchSearchVisited(this.id).subscribe({ next: () => { }, error: () => { } });
        if (m.total === 0 && this.search) {
          this.account.estimateSearch(this.search.criteria).subscribe({ next: (e) => (this.forSale = e), error: () => { } });
        }
      },
      error: () => (this.state = 'error'),
    });
  }

  public get rows(): SavedSearchPlate[] {
    const items = this.matches?.items || [];
    return this.showOnlyNew ? items.filter((p) => p.isNew) : items;
  }

  public get pages(): number {
    return this.matches ? Math.max(1, Math.ceil(this.matches.total / this.pageSize)) : 1;
  }

  public goPage(p: number): void {
    this.page = Math.min(Math.max(1, p), this.pages);
    this.loadMatches(false);
  }

  public money(pence: number | null): string {
    return pence == null ? 'Price on request' : '£' + Math.round(pence / 100).toLocaleString('en-GB');
  }

  public what(p: SavedSearchPlate): string {
    switch (p.eventType) {
      case 'price_change':
        return p.previousAllInPence != null && p.allInPence != null && p.allInPence < p.previousAllInPence
          ? `Price dropped from ${this.money(p.previousAllInPence)}` : 'Price changed';
      case 'becomes_available': return 'Now available';
      case 'auction_result':
      case 'verified_sale': return 'Sold at auction';
      default: return 'New listing';
    }
  }

  /** Plate as written: "AB 12", "AB12 CDE" (same rule as the digest email). */
  public display(reg: string): string {
    const r = (reg || '').toUpperCase();
    let m = r.match(/^([A-Z]{2}[0-9]{2})([A-Z]{3})$/) || r.match(/^([A-Z][0-9]{1,3})([A-Z]{3})$/)
      || r.match(/^([A-Z]{3})([0-9]{1,3}[A-Z])$/) || r.match(/^([A-Z]+|[0-9]+)([A-Z]+|[0-9]+)$/);
    return m ? `${m[1]} ${m[2]}` : r;
  }
}
